import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Ban,
  Building2,
  ChevronDown,
  ChevronRight,
  FileText,
  MessageCircle,
  StickyNote,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ComentariosDialog } from '@/components/domain/ComentariosDialog';
import { fmtShortDate, fmtDateTimeMty } from '@/lib/format';
import { cn } from '@/lib/utils';

// ─── Tipos del API CxC ─────────────────────────────────────────────────────
type EstatusCliente = 'activo' | 'legal' | 'afs' | 'incobrable';

const ESTATUS_CLIENTE_LABEL: Record<EstatusCliente, string> = {
  activo: 'Activo',
  legal: 'Legal',
  afs: 'AFS',
  incobrable: 'Incobrable',
};

const ESTATUS_CLIENTE_CLS: Record<EstatusCliente, string> = {
  activo: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  legal: 'border-purple-300 bg-purple-50 text-purple-700',
  afs: 'border-amber-300 bg-amber-50 text-amber-800',
  incobrable: 'border-red-300 bg-red-50 text-red-700',
};

interface ClienteCard {
  cliente: string;
  norm: string;
  nFacturas: number;
  nVencidas: number;
  montoTotal: Record<string, number>;
  montoVencido: Record<string, number>;
  tareasPendientes: number;
  diasCredito: number | null;
  estatus: EstatusCliente;
  tieneNotasFacturacion: boolean;
}

interface FacturaCxc {
  id: number;
  numero: string;
  /** Folio CONTPAQ (Compliance Number) — el número fiscal que ve el cliente. */
  folioContpaq?: string | null;
  entidad: string;
  monto: number;
  moneda: string;
  fechaVencimiento: string | null;
  estatus: 'pagada' | 'vigente' | 'vencida';
  diasVencida: number | null;
  bookingIdExterno?: string | null;
}

interface EmbarqueCxc {
  id: number;
  bookingIdExterno: string;
  transporte: string;
  modo: string;
  direccion: string;
  serviceLevel: string | null;
  estadoCxc: 'abierto' | 'cerrado';
  etd: string | null;
  eta: string | null;
  fechaRecepcion: string | null;
  operativo: string | null;
  comercial: string | null;
  pteFacturar: number | null;
  desgloseFacturar: string | null;
  limiteFacturar: string | null;
  detenido: boolean;
  comentarios: number;
  tareasCobranza: number;
  tareasCobranzaPendientes: number;
  facturas: FacturaCxc[];
}

interface ClienteDetalle {
  cliente: string;
  norm: string;
  buckets: Record<string, Record<string, number>>;
  estatus: EstatusCliente;
  credito: { limite: number | null; moneda: string | null; dias: number | null } | null;
  notasFacturacion: { notas: string; actualizadoPor: string | null; updatedAt: string } | null;
  embarques: EmbarqueCxc[];
  facturasSinEmbarque: FacturaCxc[];
}

interface ChecklistItem {
  id: number;
  hierarchyId: string;
  parentKey: string | null;
  templateKey: string;
  nombre: string;
  completado: boolean;
  completadoPor: string | null;
  completadoAt: string | null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────
/** {"USD": 45000, "MXN": 12000} → "45,000 USD · 12,000 MXN" (— si vacío) */
function fmtMontos(m: Record<string, number> | undefined): string {
  const entries = Object.entries(m ?? {}).filter(([, v]) => Math.abs(v) >= 0.01);
  if (!entries.length) return '—';
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cur, v]) => `${v.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${cur}`)
    .join(' · ');
}

const BUCKET_LABELS: [string, string][] = [
  ['por_vencer', 'Por vencer'],
  ['d1_15', '1-15 días'],
  ['d16_30', '16-30 días'],
  ['d31_45', '31-45 días'],
  ['d46_60', '46-60 días'],
  ['d61_90', '61-90 días'],
  ['d90_mas', '+90 días'],
];

function FacturaChip({ f }: { f: FacturaCxc }) {
  const cls =
    f.estatus === 'pagada'
      ? 'bg-emerald-600 text-white'
      : f.estatus === 'vencida'
        ? 'bg-red-600 text-white'
        : 'bg-amber-500 text-white';
  const label =
    f.estatus === 'pagada'
      ? 'PAGADA'
      : f.estatus === 'vencida'
        ? `VENCIDA · ${f.diasVencida}d`
        : 'NO PAGADA';
  // El folio CONTPAQ ya viene con prefijo (ej. "F-22558"); el fallback usa la
  // referencia interna de CW prefijada.
  const num = f.folioContpaq || (f.numero ? `F-${f.numero}` : '');
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px]"
      title={`${f.entidad} T-${f.numero}${f.folioContpaq ? ` · CONTPAQ ${f.folioContpaq}` : ''} · ${f.monto.toLocaleString()} ${f.moneda}${
        f.fechaVencimiento ? ` · vence ${fmtShortDate(f.fechaVencimiento)}` : ''
      }`}
    >
      {num && <span className="text-ink-500 tabular">{num}</span>}
      <span className={cn('rounded-full px-2 py-0.5 font-semibold', cls)}>{label}</span>
    </span>
  );
}

// ─── Página 1: Clientes (tarjetas) ─────────────────────────────────────────
export function CxcClientesPage() {
  const [buscar, setBuscar] = useState('');
  const [fEstatus, setFEstatus] = useState('__all__');
  const q = useQuery({
    queryKey: ['cxc-clientes', buscar, fEstatus],
    queryFn: () => {
      const p = new URLSearchParams();
      if (buscar) p.set('buscar', buscar);
      if (fEstatus !== '__all__') p.set('estatus', fEstatus);
      const qs = p.toString();
      return api<ClienteCard[]>(`/cxc/clientes${qs ? `?${qs}` : ''}`);
    },
  });
  const [notasDe, setNotasDe] = useState<ClienteCard | null>(null);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2 flex-wrap">
            <Building2 className="h-5 w-5" /> Clientes · Cobranza
            <span className="text-sm font-normal text-ink-400">
              (Información con embarques a partir de junio 2026)
            </span>
          </h1>
          <p className="text-sm text-ink-400 mt-0.5">
            Cartera por cliente: facturas, saldos vencidos y gestión de cobro.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Buscar cliente…"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            className="max-w-xs"
          />
          <Select value={fEstatus} onValueChange={setFEstatus}>
            <SelectTrigger className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los estatus</SelectItem>
              <SelectItem value="activo">Activo</SelectItem>
              <SelectItem value="legal">Legal</SelectItem>
              <SelectItem value="afs">AFS</SelectItem>
              <SelectItem value="incobrable">Incobrable</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {q.isLoading && <p className="mt-8 text-ink-400">Cargando cartera…</p>}
      {!q.isLoading && (q.data ?? []).length === 0 && (
        <p className="mt-8 text-ink-400">
          Sin clientes con facturas registradas{buscar ? ' para esa búsqueda' : ''}.
        </p>
      )}

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(q.data ?? []).map((c) => (
          <div
            key={c.norm}
            className="rounded-lg border border-ink-200 bg-surface-1 p-4 hover:border-brand-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-center gap-2 flex-wrap min-w-0">
                <Link
                  to={`/clientes/${encodeURIComponent(c.norm)}`}
                  className="text-lg font-bold text-ink-900 hover:text-brand-600 leading-tight"
                >
                  {c.cliente}
                </Link>
                {c.estatus !== 'activo' && (
                  <span
                    className={cn(
                      'rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase',
                      ESTATUS_CLIENTE_CLS[c.estatus],
                    )}
                  >
                    {ESTATUS_CLIENTE_LABEL[c.estatus]}
                  </span>
                )}
              </span>
              <button
                onClick={() => setNotasDe(c)}
                className={cn(
                  'shrink-0 inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-medium uppercase tracking-wide',
                  c.tieneNotasFacturacion
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-ink-200 text-ink-500 hover:bg-surface-2',
                )}
                title="Instrucciones de facturación del cliente (POD, portal, firma…)"
              >
                <StickyNote className="h-3 w-3" />
                Notas de facturación
              </button>
            </div>
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
              {[
                { v: String(c.nFacturas), l: 'Nº de facturas', cls: '' },
                { v: String(c.nVencidas), l: 'Facturas vencidas', cls: c.nVencidas > 0 ? 'text-red-600' : '' },
                { v: fmtMontos(c.montoTotal), l: 'Monto total', cls: '' },
                { v: fmtMontos(c.montoVencido), l: 'Monto vencido', cls: Object.keys(c.montoVencido).length ? 'text-red-600' : '' },
                { v: String(c.tareasPendientes), l: 'Tareas pendientes', cls: '' },
                { v: c.diasCredito != null ? String(c.diasCredito) : '—', l: 'Días de crédito', cls: '' },
              ].map((kpi) => (
                <div key={kpi.l} className="rounded-md border border-ink-100 bg-surface-2 px-1.5 py-2">
                  <div className={cn('text-sm font-bold leading-tight', kpi.cls)}>{kpi.v}</div>
                  <div className="text-[9px] uppercase tracking-wide text-ink-400 mt-0.5">{kpi.l}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {notasDe && (
        <NotasFacturacionModal
          norm={notasDe.norm}
          cliente={notasDe.cliente}
          onClose={() => setNotasDe(null)}
        />
      )}
    </div>
  );
}

// ─── Modal de notas de facturación ─────────────────────────────────────────
function NotasFacturacionModal({
  norm,
  cliente,
  onClose,
}: {
  norm: string;
  cliente: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const puedeEditar = user?.rol === 'cobranza' || user?.rol === 'admin' || user?.rol === 'director';
  const detalleQ = useQuery({
    queryKey: ['cxc-cliente', norm],
    queryFn: () => api<ClienteDetalle>(`/cxc/clientes/${encodeURIComponent(norm)}`),
  });
  const [texto, setTexto] = useState<string | null>(null);
  const actual = detalleQ.data?.notasFacturacion;
  const valor = texto ?? actual?.notas ?? '';

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/cxc/clientes/${encodeURIComponent(norm)}/notas-facturacion`, {
        method: 'PUT',
        body: JSON.stringify({ nombre: cliente, notas: valor }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cxc-cliente', norm] });
      qc.invalidateQueries({ queryKey: ['cxc-clientes'] });
      toast.success('Notas de facturación guardadas');
      onClose();
    },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Error al guardar'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-lg bg-surface-1 border border-ink-200 shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold flex items-center gap-2">
          <StickyNote className="h-4 w-4" /> Notas de facturación · {cliente}
        </h3>
        <p className="text-xs text-ink-400 mt-1">
          Instrucciones para facturarle a este cliente: si requiere POD, subir a portal,
          factura firmada, etc.
        </p>
        <textarea
          value={valor}
          onChange={(e) => setTexto(e.target.value)}
          disabled={!puedeEditar}
          rows={6}
          maxLength={8000}
          placeholder="Ej. Requiere POD firmado y subir factura al portal Coupa antes del día 25…"
          className="mt-3 w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none disabled:bg-surface-2"
        />
        {actual && (
          <p className="text-[11px] text-ink-400 mt-1">
            Última edición: {actual.actualizadoPor ?? '—'} · {fmtDateTimeMty(actual.updatedAt)}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          {puedeEditar && (
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Página 2: board de cobranza de un cliente ─────────────────────────────
export function CxcClientePage() {
  const { norm = '' } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['cxc-cliente', norm],
    queryFn: () => api<ClienteDetalle>(`/cxc/clientes/${encodeURIComponent(norm)}`),
  });

  const [buscar, setBuscar] = useState('');
  const [fTransporte, setFTransporte] = useState('__all__');
  const [fDireccion, setFDireccion] = useState('__all__');
  const [fFacturas, setFFacturas] = useState('pendientes'); // todas | pendientes | vencidas
  const [fEstado, setFEstado] = useState('__all__'); // abierto | cerrado
  const [abierto, setAbierto] = useState<number | null>(null);
  const [comentariosDe, setComentariosDe] = useState<EmbarqueCxc | null>(null);
  const [notasOpen, setNotasOpen] = useState(false);
  const [comentariosClienteOpen, setComentariosClienteOpen] = useState(false);
  const puedeGestionar = user?.rol === 'cobranza' || user?.rol === 'admin' || user?.rol === 'director';

  const estatusMut = useMutation({
    mutationFn: (estatus: string) =>
      api(`/cxc/clientes/${encodeURIComponent(norm)}/estatus`, {
        method: 'PATCH',
        body: JSON.stringify({ nombre: q.data?.cliente ?? norm, estatus }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cxc-cliente', norm] });
      qc.invalidateQueries({ queryKey: ['cxc-clientes'] });
      toast.success('Estatus del cliente actualizado');
    },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Error'),
  });

  const cancelarCreditoMut = useMutation({
    mutationFn: () =>
      api<{ to: string[]; subject: string; body: string }>(
        `/cxc/clientes/${encodeURIComponent(norm)}/cancelar-credito-draft`,
      ),
    onSuccess: (d) => {
      // Gmail compose en ventana nueva (como la app de Quotes), todo precargado.
      const url =
        'https://mail.google.com/mail/?view=cm&fs=1' +
        `&to=${encodeURIComponent(d.to.join(','))}` +
        `&su=${encodeURIComponent(d.subject)}` +
        `&body=${encodeURIComponent(d.body)}`;
      window.open(url, '_blank', 'noopener');
      qc.invalidateQueries({ queryKey: ['cxc-comentarios-cliente', norm] });
      toast.success(`Borrador abierto (${d.to.length} destinatarios) — revísalo y envíalo desde Gmail`);
    },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'No se pudo generar el borrador'),
  });

  const embarques = useMemo(() => {
    let rows = q.data?.embarques ?? [];
    const s = buscar.trim().toUpperCase();
    if (s)
      rows = rows.filter(
        (e) =>
          e.bookingIdExterno.toUpperCase().includes(s) ||
          e.facturas.some((f) => f.numero.includes(s)),
      );
    if (fTransporte !== '__all__') rows = rows.filter((e) => e.transporte === fTransporte);
    if (fDireccion !== '__all__') rows = rows.filter((e) => e.direccion === fDireccion);
    if (fEstado !== '__all__') rows = rows.filter((e) => e.estadoCxc === fEstado);
    if (fFacturas === 'pendientes')
      rows = rows.filter((e) => e.facturas.some((f) => f.estatus !== 'pagada'));
    if (fFacturas === 'vencidas')
      rows = rows.filter((e) => e.facturas.some((f) => f.estatus === 'vencida'));
    return rows;
  }, [q.data, buscar, fTransporte, fDireccion, fFacturas, fEstado]);

  if (q.isLoading) return <div className="p-6 text-ink-400">Cargando cliente…</div>;
  if (!q.data) return <div className="p-6 text-ink-400">Cliente no encontrado.</div>;
  const d = q.data;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/clientes" className="text-sm text-brand-600 hover:underline">← Clientes</Link>
          <h1 className="text-xl font-bold">{d.cliente}</h1>
          {puedeGestionar && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `¿Generar el borrador de CANCELACIÓN DE CRÉDITO para ${d.cliente}?\n` +
                      'Se abrirá en Gmail con destinatarios y texto precargados; tú lo revisas y lo envías.',
                  )
                )
                  cancelarCreditoMut.mutate();
              }}
              disabled={cancelarCreditoMut.isPending}
              className="rounded border-2 border-red-500 text-red-600 hover:bg-red-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide"
            >
              Cancelar crédito
            </button>
          )}
          {/* Estatus del Cliente (manual): editable por cobranza/admin */}
          {puedeGestionar ? (
            <Select value={d.estatus} onValueChange={(v) => estatusMut.mutate(v)}>
              <SelectTrigger
                className={cn(
                  'h-7 w-32 text-[11px] font-semibold uppercase border-2 rounded-full',
                  ESTATUS_CLIENTE_CLS[d.estatus],
                )}
                title="Estatus del Cliente"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ESTATUS_CLIENTE_LABEL) as EstatusCliente[]).map((k) => (
                  <SelectItem key={k} value={k}>{ESTATUS_CLIENTE_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase',
                ESTATUS_CLIENTE_CLS[d.estatus],
              )}
            >
              {ESTATUS_CLIENTE_LABEL[d.estatus]}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Crédito del cliente (AR Profile: límite + días) */}
          <span
            className="rounded-md border border-ink-200 bg-surface-2 px-2.5 py-1 text-[11px] text-ink-700"
            title="Límite de crédito (AR Profile de CargoWise)"
          >
            Crédito:{' '}
            <b>
              {d.credito && d.credito.limite && d.credito.limite > 0
                ? `$${d.credito.limite.toLocaleString('en-US')} ${d.credito.moneda ?? ''}`
                : 'Sin crédito (contado)'}
            </b>
            {d.credito?.dias != null && d.credito.dias > 0 && ` · ${d.credito.dias} días`}
          </span>
          <button
            onClick={() => setComentariosClienteOpen(true)}
            className="inline-flex items-center gap-1 rounded border border-ink-900 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-ink-800 hover:bg-surface-2"
            title="Comentarios de gestión de cobranza del cliente (llamadas, emails, whatsapp)"
          >
            <MessageCircle className="h-3 w-3" /> Comentarios por cliente
          </button>
          <button
            onClick={() => setNotasOpen(true)}
            className={cn(
              'inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-medium uppercase tracking-wide',
              d.notasFacturacion
                ? 'border-brand-300 bg-brand-50 text-brand-700'
                : 'border-ink-200 text-ink-500 hover:bg-surface-2',
            )}
          >
            <StickyNote className="h-3 w-3" /> Notas de facturación
          </button>
        </div>
      </div>

      {/* Buckets de antigüedad */}
      <div className="mt-4 flex flex-wrap gap-2">
        {BUCKET_LABELS.map(([k, label]) => {
          const montos = d.buckets[k] ?? {};
          const vacio = Object.keys(montos).length === 0;
          const esVencido = k !== 'por_vencer';
          return (
            <div
              key={k}
              className={cn(
                'rounded-md border px-3 py-1.5 text-center min-w-[104px]',
                vacio
                  ? 'border-ink-100 bg-surface-2 text-ink-300'
                  : esVencido
                    ? 'border-red-200 bg-red-50'
                    : 'border-ink-200 bg-surface-2',
              )}
            >
              <div className="text-[10px] uppercase tracking-wide text-ink-400">{label}</div>
              <div className={cn('text-xs font-bold mt-0.5', !vacio && esVencido && 'text-red-700')}>
                {fmtMontos(montos)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Buscar Booking ID o número de factura…"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          className="max-w-sm"
        />
        <Select value={fTransporte} onValueChange={setFTransporte}>
          <SelectTrigger className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los transportes</SelectItem>
            <SelectItem value="SEA">SEA</SelectItem>
            <SelectItem value="AIR">AIR</SelectItem>
            <SelectItem value="ROA">ROA</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fDireccion} onValueChange={setFDireccion}>
          <SelectTrigger className="h-9 w-32 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Imp + Exp</SelectItem>
            <SelectItem value="Import">Import</SelectItem>
            <SelectItem value="Export">Export</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fFacturas} onValueChange={setFFacturas}>
          <SelectTrigger className="h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pendientes">Pendientes</SelectItem>
            <SelectItem value="vencidas">Con vencidas</SelectItem>
            <SelectItem value="todas">Todas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fEstado} onValueChange={setFEstado}>
          <SelectTrigger className="h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los envíos</SelectItem>
            <SelectItem value="abierto">Abiertos</SelectItem>
            <SelectItem value="cerrado">Cerrados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Embarques */}
      <div className="mt-4 space-y-3">
        {embarques.length === 0 && (
          <p className="text-ink-400 text-sm py-6">Sin embarques para esos filtros.</p>
        )}
        {embarques.map((e) => (
          <EmbarqueCard
            key={e.id}
            e={e}
            expanded={abierto === e.id}
            onToggle={() => setAbierto(abierto === e.id ? null : e.id)}
            onComentarios={() => setComentariosDe(e)}
            puedePalomar={user?.rol === 'cobranza' || user?.rol === 'admin' || user?.rol === 'director'}
            puedeAlertar={
              user?.rol === 'cobranza' ||
              user?.rol === 'admin' ||
              user?.rol === 'director' ||
              user?.rol === 'supervisor' ||
              (user?.rol === 'gerente' && user?.gerenteTipo === 'ventas')
            }
            onChecklistChange={() => {
              qc.invalidateQueries({ queryKey: ['cxc-cliente', norm] });
              qc.invalidateQueries({ queryKey: ['cxc-embarque', e.id] });
            }}
          />
        ))}
      </div>

      {/* Facturas sin embarque en la app */}
      {d.facturasSinEmbarque.length > 0 && (
        <div className="mt-6 rounded-lg border border-ink-200 bg-surface-1 p-4">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Facturas sin embarque en la app ({d.facturasSinEmbarque.length})
          </h3>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5">
            {d.facturasSinEmbarque.map((f) => (
              <FacturaChip key={f.id} f={f} />
            ))}
          </div>
        </div>
      )}

      {comentariosDe && (
        <ComentariosDialog
          open
          onOpenChange={(o) => !o && setComentariosDe(null)}
          bookingId={comentariosDe.id}
          bookingIdExterno={comentariosDe.bookingIdExterno}
          cliente={d.cliente}
        />
      )}
      {comentariosClienteOpen && (
        <ComentariosClienteDialog
          norm={norm}
          cliente={d.cliente}
          puedeEscribir={puedeGestionar}
          onClose={() => setComentariosClienteOpen(false)}
        />
      )}
      {notasOpen && (
        <NotasFacturacionModal norm={norm} cliente={d.cliente} onClose={() => setNotasOpen(false)} />
      )}
    </div>
  );
}

// ─── Comentarios de gestión por CLIENTE (chat, histórico append-only) ──────
function ComentariosClienteDialog({
  norm,
  cliente,
  puedeEscribir,
  onClose,
}: {
  norm: string;
  cliente: string;
  puedeEscribir: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const q = useQuery({
    queryKey: ['cxc-comentarios-cliente', norm],
    queryFn: () =>
      api<{ comentarios: { id: number; texto: string; createdAt: string; autor: { nombre: string } }[] }>(
        `/cxc/clientes/${encodeURIComponent(norm)}/comentarios`,
      ),
  });
  const addMut = useMutation({
    mutationFn: (t: string) =>
      api(`/cxc/clientes/${encodeURIComponent(norm)}/comentarios`, {
        method: 'POST',
        body: JSON.stringify({ nombre: cliente, texto: t }),
      }),
    onSuccess: () => {
      setTexto('');
      qc.invalidateQueries({ queryKey: ['cxc-comentarios-cliente', norm] });
    },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'No se pudo enviar'),
  });
  const enviar = () => {
    const t = texto.trim();
    if (t) addMut.mutate(t);
  };
  const comentarios = q.data?.comentarios ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-xl rounded-lg bg-surface-1 border border-ink-200 shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold flex items-center gap-2">
          <MessageCircle className="h-4 w-4" /> Comentarios por cliente · {cliente}
        </h3>
        <p className="text-xs text-ink-400 mt-1">
          Gestión de cobranza (llamada, email, whatsapp): compromisos de pago, negativas,
          acuerdos. Histórico con fecha y hora; el más reciente arriba.
        </p>
        {puedeEscribir && (
          <div className="mt-3 flex items-end gap-2">
            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  enviar();
                }
              }}
              placeholder="Ej. Cliente menciona que pagará lo vencido el próximo 7 de agosto…  (Ctrl+Enter)"
              rows={2}
              maxLength={4000}
              className="flex-1 rounded-md border border-ink-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
            />
            <Button onClick={enviar} disabled={addMut.isPending || !texto.trim()}>
              {addMut.isPending ? 'Enviando…' : 'Agregar'}
            </Button>
          </div>
        )}
        <div className="mt-3 space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {q.isLoading && <p className="text-sm text-ink-400">Cargando…</p>}
          {!q.isLoading && comentarios.length === 0 && (
            <p className="text-sm text-ink-400 italic">Sin comentarios de gestión todavía.</p>
          )}
          {comentarios.map((c) => (
            <div key={c.id} className="rounded-md border border-ink-100 bg-surface-2 px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-semibold text-ink-800">{c.autor?.nombre ?? 'Usuario'}</span>
                <span className="text-[11px] text-ink-400 tabular">{fmtDateTimeMty(c.createdAt)}</span>
              </div>
              <p className="mt-0.5 text-sm text-ink-700 whitespace-pre-wrap break-words">{c.texto}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Tarjeta de embarque ───────────────────────────────────────────────────
function EmbarqueCard({
  e,
  expanded,
  onToggle,
  onComentarios,
  puedePalomar,
  puedeAlertar,
  onChecklistChange,
}: {
  e: EmbarqueCxc;
  expanded: boolean;
  onToggle: () => void;
  onComentarios: () => void;
  puedePalomar: boolean;
  puedeAlertar: boolean;
  onChecklistChange: () => void;
}) {
  return (
    <div className="rounded-lg border border-ink-200 bg-surface-1">
      <div className="p-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-2 flex-wrap">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-ink-400 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-ink-400 shrink-0" />
          )}
          <span className="font-bold text-sm">{e.bookingIdExterno}</span>
          <span className="text-[11px] text-ink-400">
            {e.transporte} · {e.modo} · {e.direccion}
          </span>
          {e.serviceLevel && (
            <span className="rounded bg-sky-100 text-sky-700 text-[10px] font-semibold px-1.5 py-0.5">
              {e.serviceLevel}
            </span>
          )}
          <span
            className={cn(
              'rounded-full border px-2 py-0.5 text-[11px] font-medium',
              e.estadoCxc === 'cerrado'
                ? 'border-ink-200 text-ink-500 bg-surface-2'
                : 'border-emerald-300 text-emerald-700 bg-emerald-50',
            )}
          >
            {e.estadoCxc === 'cerrado' ? 'Cerrado' : 'Abierto'}
          </span>
          {e.detenido && (
            <span className="inline-flex items-center gap-1 text-red-700 text-[11px] font-semibold">
              <Ban className="h-3 w-3" /> Detenido
            </span>
          )}
          <div className="ml-auto flex items-center gap-2" onClick={(ev) => ev.stopPropagation()}>
            <button
              onClick={onComentarios}
              className="inline-flex items-center gap-1 rounded border border-ink-200 px-2 py-0.5 text-[11px] text-ink-600 hover:bg-surface-2"
            >
              <MessageCircle className="h-3 w-3" /> Comentarios ({e.comentarios})
            </button>
            {puedeAlertar && <AlertaCxcButton e={e} onDone={onChecklistChange} />}
            {e.tareasCobranza > 0 && (
              <span
                className={cn(
                  'text-[11px] rounded px-1.5 py-0.5',
                  e.tareasCobranzaPendientes > 0
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-emerald-100 text-emerald-800',
                )}
              >
                {e.tareasCobranza - e.tareasCobranzaPendientes}/{e.tareasCobranza} cobranza
              </span>
            )}
          </div>
        </div>
        <div className="mt-1.5 text-xs text-ink-500 flex flex-wrap gap-x-3 gap-y-0.5 pl-6">
          <span>Operativo: <b className="text-ink-700">{e.operativo ?? '—'}</b></span>
          <span>Comercial: <b className="text-ink-700">{e.comercial ?? '—'}</b></span>
          <span>ETD {fmtShortDate(e.etd)}</span>
          <span>ETA {fmtShortDate(e.eta)}</span>
          <span>Recibido {fmtShortDate(e.fechaRecepcion)}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 pl-6">
          <span className="text-xs" title={e.desgloseFacturar ?? undefined}>
            Pte. facturar:{' '}
            <b className={cn((e.pteFacturar ?? 0) > 0 ? 'text-amber-700' : 'text-ink-600')}>
              ${(e.pteFacturar ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD
            </b>
            {e.limiteFacturar && (
              <span className="text-ink-400"> (límite {fmtShortDate(e.limiteFacturar)})</span>
            )}
          </span>
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            {e.facturas.length === 0 && (
              <span className="text-[11px] text-ink-300">sin facturas ligadas</span>
            )}
            {e.facturas.map((f) => (
              <FacturaChip key={f.id} f={f} />
            ))}
          </span>
        </div>
      </div>

      {expanded && (
        <EmbarqueDetalle id={e.id} puedePalomar={puedePalomar} onChecklistChange={onChecklistChange} />
      )}
    </div>
  );
}

// ─── Alerta CxC para detención de embarque (sugerencia, envía correo) ──────
function AlertaCxcButton({ e, onDone }: { e: EmbarqueCxc; onDone: () => void }) {
  const mut = useMutation({
    mutationFn: (force: boolean) =>
      api<{ ok: boolean; alertaPrevia24h?: boolean; destinatarios?: number }>(
        `/cxc/embarques/${e.id}/alerta-detencion`,
        { method: 'POST', body: JSON.stringify({ force }) },
      ),
    onSuccess: (r) => {
      if (!r.ok && r.alertaPrevia24h) {
        if (
          window.confirm(
            'Ya se envió una alerta CxC para este embarque en las últimas 24 horas.\n¿Enviar de nuevo?',
          )
        ) {
          mut.mutate(true);
        }
        return;
      }
      toast.success(`Alerta CxC enviada (${r.destinatarios} notificados)`);
      onDone();
    },
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo enviar la alerta'),
  });
  return (
    <button
      onClick={() => {
        if (
          window.confirm(
            `¿Enviar la ALERTA CxC para el embarque ${e.bookingIdExterno}?\n\n` +
              'Se notificará al comercial, operativo, gerencias y AP la SUGERENCIA de ' +
              'detener el embarque por falta de pago. NO es una instrucción de detención.',
          )
        )
          mut.mutate(false);
      }}
      disabled={mut.isPending}
      className="rounded border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold leading-tight text-center"
      title="Sugerir detención por falta de pago (correo a comercial, operativo, gerencias y AP)"
    >
      Alerta CxC para
      <br />
      detención de Embarque
    </button>
  );
}

// ─── Detalle expandido: facturas + checklist + último comentario ───────────
function EmbarqueDetalle({
  id,
  puedePalomar,
  onChecklistChange,
}: {
  id: number;
  puedePalomar: boolean;
  onChecklistChange: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['cxc-embarque', id],
    queryFn: () =>
      api<{
        facturas: FacturaCxc[];
        checklist: ChecklistItem[];
        ultimoComentario: { texto: string; autor: string; createdAt: string } | null;
        desgloseFacturar: string | null;
      }>(`/cxc/embarques/${id}`),
  });

  const toggleMut = useMutation({
    mutationFn: ({ tareaId, completado }: { tareaId: number; completado: boolean }) =>
      api(`/cxc/tareas/${tareaId}`, { method: 'PATCH', body: JSON.stringify({ completado }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cxc-embarque', id] });
      onChecklistChange();
    },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'No se pudo actualizar'),
  });

  if (q.isLoading) return <div className="border-t border-ink-100 p-4 text-xs text-ink-400">Cargando…</div>;
  const d = q.data;
  if (!d) return null;

  // checklist agrupado: padres (sin parentKey) con sus hijas
  const padres = d.checklist.filter((t) => !t.parentKey);
  const hijasDe = (k: string) => d.checklist.filter((t) => t.parentKey === k);

  return (
    <div className="border-t border-ink-100 p-4 space-y-4">
      {/* Facturas con días de vencimiento */}
      {d.facturas.length > 0 && (
        <table className="text-xs w-full max-w-xl">
          <thead>
            <tr className="text-ink-400 uppercase text-[10px] tracking-wide text-left">
              <th className="py-1 pr-4">No. Factura</th>
              <th className="py-1 pr-4">Estatus</th>
              <th className="py-1 pr-4">Monto</th>
              <th className="py-1">Días de vencimiento</th>
            </tr>
          </thead>
          <tbody>
            {d.facturas.map((f) => (
              <tr key={f.id} className="border-t border-ink-100">
                <td className="py-1.5 pr-4 tabular font-medium">
                  {f.folioContpaq ?? `F-${f.numero}`}{' '}
                  <span className="text-ink-300">
                    ({f.entidad}{f.folioContpaq ? ` · T-${f.numero}` : ''})
                  </span>
                </td>
                <td className="py-1.5 pr-4"><FacturaChip f={{ ...f, numero: '', folioContpaq: null }} /></td>
                <td className="py-1.5 pr-4 tabular">{f.monto.toLocaleString()} {f.moneda}</td>
                <td className="py-1.5">
                  <span className="rounded-full bg-ink-100 text-ink-600 px-2 py-0.5 text-[11px]">
                    {f.diasVencida == null
                      ? f.estatus === 'pagada' ? '—' : 'sin fecha'
                      : f.diasVencida <= 0
                        ? `${f.diasVencida} días`
                        : `${f.diasVencida} días`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Último comentario de gestión */}
      <div className="rounded-md border border-ink-100 bg-surface-2 px-3 py-2 max-w-xl">
        <div className="text-[10px] uppercase tracking-wide text-ink-400">Última gestión de cobranza</div>
        {d.ultimoComentario ? (
          <>
            <p className="text-sm text-ink-700 mt-0.5 whitespace-pre-wrap">{d.ultimoComentario.texto}</p>
            <p className="text-[11px] text-ink-400 mt-0.5">
              {d.ultimoComentario.autor} · {fmtDateTimeMty(d.ultimoComentario.createdAt)}
            </p>
          </>
        ) : (
          <p className="text-sm text-ink-400 italic mt-0.5">Sin comentarios aún.</p>
        )}
      </div>

      {/* Checklist de cobranza (de la plantilla; vacío hasta que exista el archivo) */}
      {d.checklist.length > 0 ? (
        <div className="max-w-xl">
          <div className="text-[10px] uppercase tracking-wide text-ink-400 mb-1">Checklist de cobranza</div>
          {padres.map((p) => (
            <div key={p.id} className="mb-1.5">
              <label className="flex items-center gap-2 text-sm font-medium text-ink-800">
                <input
                  type="checkbox"
                  checked={p.completado}
                  disabled={!puedePalomar || toggleMut.isPending}
                  onChange={(ev) => toggleMut.mutate({ tareaId: p.id, completado: ev.target.checked })}
                />
                {p.hierarchyId} {p.nombre}
                {p.completado && p.completadoPor && (
                  <span className="text-[10px] text-ink-400 font-normal">· {p.completadoPor}</span>
                )}
              </label>
              {hijasDe(p.templateKey).map((h) => (
                <label key={h.id} className="flex items-center gap-2 text-sm text-ink-600 pl-6 mt-0.5">
                  <input
                    type="checkbox"
                    checked={h.completado}
                    disabled={!puedePalomar || toggleMut.isPending}
                    onChange={(ev) => toggleMut.mutate({ tareaId: h.id, completado: ev.target.checked })}
                  />
                  {h.hierarchyId} {h.nombre}
                  {h.completado && h.completadoPor && (
                    <span className="text-[10px] text-ink-400 font-normal">· {h.completadoPor}</span>
                  )}
                </label>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-ink-300 italic">
          Checklist de cobranza pendiente de plantilla (se generará automáticamente cuando exista el archivo).
        </p>
      )}
    </div>
  );
}
